import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Gift, Copy, Check, Loader2, Users, Zap, Trophy, Percent } from 'lucide-react';
import { memberApi } from './memberApi';

export const RewardsTab = ({ user }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    memberApi.get('/member/rewards').then(r => setData(r.data)).catch(() => setError(true));
  }, []);

  const copyCode = () => {
    navigator.clipboard.writeText(data.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (error) {
    return <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-8 text-center text-gray-500 text-sm">Couldn't load your rewards. Please try again.</div>;
  }
  if (!data) {
    return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#C41E3A]" size={28} /></div>;
  }

  if (!data.has_code) {
    return (
      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-10 text-center" data-testid="rewards-no-code">
        <Gift className="mx-auto text-gray-600 mb-4" size={36} />
        <h3 className="font-['Oswald'] text-lg uppercase tracking-wide text-white mb-2">No Referral Code Yet</h3>
        <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">Become a CWF Ambassador: get your own code, share it with friends, and earn points toward bigger discounts every time it's used.</p>
        <Link to="/#ambassador" className="inline-block bg-[#C41E3A] hover:bg-[#A01830] text-white text-xs font-bold uppercase tracking-wider px-6 py-3 rounded-sm transition-colors" data-testid="rewards-signup-link">
          Become an Ambassador
        </Link>
      </div>
    );
  }

  const pct = Math.min(100, Math.round((data.points / data.goal) * 100));

  return (
    <div className="space-y-4" data-testid="rewards-tab">
      {data.event_active && (
        <div className="bg-[#C41E3A]/10 border border-[#C41E3A] rounded-sm p-3 flex items-center gap-2 text-sm text-white" data-testid="rewards-event-banner">
          <Zap size={15} className="text-[#D4AF37]" /> <span className="font-bold text-[#D4AF37]">2× POINTS EVENT ACTIVE</span> — every use of your code earns double points right now!
        </div>
      )}

      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-6">
        <h3 className="font-['Oswald'] text-sm uppercase tracking-[0.15em] text-[#D4AF37] mb-3">Your Referral Code</h3>
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-[#121212] border border-[#27272A] rounded-sm px-4 py-3 font-['Oswald'] text-xl md:text-2xl tracking-[0.2em] text-white uppercase" data-testid="rewards-code">{data.code}</div>
          <button onClick={copyCode} className="flex items-center gap-1.5 bg-[#C41E3A] hover:bg-[#A01830] text-white text-xs font-bold uppercase tracking-wider px-4 py-3.5 rounded-sm transition-colors flex-shrink-0" data-testid="rewards-copy-btn">
            {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <p className="text-gray-500 text-xs mt-2">Friends get a discount when they use it — you earn points on every use.</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-4 text-center" data-testid="rewards-uses">
          <Users className="mx-auto text-gray-500 mb-1.5" size={16} />
          <p className="font-['Oswald'] text-2xl text-white">{data.uses}</p>
          <p className="text-gray-500 text-[10px] uppercase tracking-wider">Referrals</p>
        </div>
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-4 text-center" data-testid="rewards-points">
          <Trophy className="mx-auto text-[#D4AF37] mb-1.5" size={16} />
          <p className="font-['Oswald'] text-2xl text-[#D4AF37]">{data.points}</p>
          <p className="text-gray-500 text-[10px] uppercase tracking-wider">Points</p>
        </div>
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-4 text-center" data-testid="rewards-discount">
          <Percent className="mx-auto text-[#C41E3A] mb-1.5" size={16} />
          <p className="font-['Oswald'] text-2xl text-white">{data.current_discount}%</p>
          <p className="text-gray-500 text-[10px] uppercase tracking-wider">Your Discount</p>
        </div>
      </div>

      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-6" data-testid="rewards-progress">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-['Oswald'] text-sm uppercase tracking-[0.15em] text-[#D4AF37]">Progress to 20% Reward</h3>
          <span className="text-gray-400 text-xs font-bold">{data.points} / {data.goal} pts</span>
        </div>
        <div className="h-2.5 bg-[#121212] border border-[#27272A] rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#C41E3A] to-[#D4AF37] transition-all duration-700" style={{ width: `${pct}%` }} />
        </div>
        {data.goal_reached ? (
          <p className="text-green-400 text-xs mt-3 flex items-center gap-1.5"><Trophy size={12} /> Goal reached — your code now gives you a 20% personal discount!</p>
        ) : (
          <p className="text-gray-500 text-xs mt-3">Reach {data.goal} points to unlock a permanent 20% personal discount.</p>
        )}
        {data.bonus_active && (
          <p className="text-[#D4AF37] text-xs mt-1.5 flex items-center gap-1.5"><Zap size={12} /> Active ambassador bonus: an extra 10% is stacked on your discount because your code was used in the last 60 days.</p>
        )}
      </div>
    </div>
  );
};
